import React, { PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './FloatingCta.css';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Button, Row, Col } from '../../importCL';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
interface IFloatingCtaProps extends MappedComponentProperties {
    targetElement?: string;
    buttonText?: string;
    buttonUrl?: string;
}

export const FloatingCtaEditConfig = {
    emptyLabel: 'Floating Cta',
    isEmpty: (props: PropsWithChildren<IFloatingCtaProps>) => {
        return !props || !props.targetElement;
    },
};

const FloatingCta = (props: PropsWithChildren<IFloatingCtaProps>): JSX.Element => {
    const [showCta, setShowCta] = useState<boolean>(false);
    const observerRef = useRef<IntersectionObserver | MutationObserver | null>(null);
    const isUsingMutationObserverRef = useRef<boolean>(false);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isResizingRef = useRef(false);

    const ensureFreshObserver = (): void => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
            isUsingMutationObserverRef.current = false;
        }
    };

    const checkValidRect = (rect: DOMRect): boolean =>
    rect.width > 0 && rect.height > 0;

    const attachIntersectionObserver = (element: Element): void => {
        const intersectionObserver = new IntersectionObserver((entries) => {
            if (!isResizingRef.current) {
                entries.forEach((entry: IntersectionObserverEntry) => {
                    const isValidRect = checkValidRect(entry.boundingClientRect);

                    if (!isValidRect) {
                        ensureFreshObserver();
                        const currentEl = document.querySelector(`.${props.targetElement}`);
                        if (currentEl) {
                            attachIntersectionObserver(currentEl);
                        }
                        return;
                    }
                    setShowCta(!entry.isIntersecting);
                })
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0,
        });

        intersectionObserver.observe(element);
        observerRef.current = intersectionObserver;
    };

    const handleResize = (): void => {
        if (resizeTimeoutRef.current) {
            clearTimeout(resizeTimeoutRef.current);
        }

        isResizingRef.current = true;

        resizeTimeoutRef.current = setTimeout(() => {
            const element = document.querySelector(`.${props.targetElement}`);
            if (!element) {
                isResizingRef.current = false;
                return;
            }

            const isValidRect = checkValidRect(element.getBoundingClientRect());

            if (!isValidRect) {
                isResizingRef.current = false;
                return;
            }

            ensureFreshObserver();
            attachIntersectionObserver(element);
            isResizingRef.current = false;
        }, 150);
    };

    useEffect(() => {
        if (!props.targetElement || props.targetElement.trim() === '') {
            return;
        }

        ensureFreshObserver();

        const element = document.querySelector(`.${props.targetElement}`);

        if (element)  {
            attachIntersectionObserver(element);
            isUsingMutationObserverRef.current = false;
        } else {
            const mutationObserver = new MutationObserver(() => {
                const targetEl = document.querySelector(`.${props.targetElement}`);
                if (targetEl && checkValidRect(targetEl.getBoundingClientRect()) && isUsingMutationObserverRef.current) {
                    isUsingMutationObserverRef.current = false;
                    mutationObserver.disconnect();
                    observerRef.current = null;
                    attachIntersectionObserver(targetEl);
                }
            });

            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
            observerRef.current = mutationObserver;
            isUsingMutationObserverRef.current = true;
        }

        window.addEventListener('resize', handleResize);

        return () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            observerRef.current = null;
            isUsingMutationObserverRef.current = false;
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div id='floating-cta' className={`FloatingCta ${showCta ? 'FloatingCta--peek': '' } ${props.isInEditor ? 'FloatingCta--editor' : ''}`}>
            <Row justify='center'>
                <Col xs={6}>
                    <Button
                        className='FloatingCta__cta'
                        id='Floating_CTASubmit'
                        size='medium'
                        href={props.buttonUrl}>
                        {transformHTMLParserLinks(wrapTrademarkSuperscript(props.buttonText)) as unknown as ReactElement}
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default FloatingCta;
